import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import type { DsrRequestType } from '@4cm/core/auth';

export interface DsrLogRow {
  id: string;
  request_type: DsrRequestType;
  requested_at: string;
  fulfilled_at: string | null;
  due_at: string;
  notes: string | null;
  /** Wave 3.5 — the data subject. Equals auth.uid() for self-service;
   *  equals the child's id for parent-on-behalf-of-child rows. */
  user_id: string;
  /** Wave 3.5 — the actor. Null for self-service (subject = actor);
   *  equals the parent's id for on-behalf rows. */
  requested_by_user_id: string | null;
}

interface State {
  data: DsrLogRow[] | null;
  loading: boolean;
  error: string | null;
}

/**
 * Reads the current user's DSR request history. RLS on `dsr_log`
 * scopes the result to `user_id = auth.uid()` automatically. Used by
 * Settings → Privacy to show the user what they've already filed +
 * the statutory deadline (`due_at`) for any pending request.
 *
 * Refetch when the caller's `version` changes — pass an incrementing
 * counter from the parent after a fileDsrRequest() call to refresh.
 */
export function useDsrRequests(version = 0): State & { refetch: () => void } {
  const [state, setState] = useState<State>({ data: null, loading: true, error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));

    (async () => {
      const { data, error } = await supabase
        .from('dsr_log')
        .select('id, request_type, requested_at, fulfilled_at, due_at, notes, user_id, requested_by_user_id')
        .order('requested_at', { ascending: false })
        .limit(20);

      if (cancelled) return;

      if (error) {
        setState({ data: null, loading: false, error: error.message });
        return;
      }
      setState({ data: (data ?? []) as DsrLogRow[], loading: false, error: null });
    })();

    return () => {
      cancelled = true;
    };
  }, [version, tick]);

  return { ...state, refetch: () => setTick((t) => t + 1) };
}
